import cn from "classnames";
import { forwardRef, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import SimpleBarReact from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import styles from "./styles.module.scss";

const OVERFLOW_THRESHOLD = 10;

type Props = {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  showGradient?: boolean;
  tabindex?: number;
};

const Scrollbar = forwardRef<HTMLDivElement, Props>(({
  children,
  className,
  showGradient = true,
  tabindex,
  ...props
}, ref) => {
  const scrollRef = useRef<HTMLElement | null>(null);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const hasOverflow = el.scrollHeight > el.clientHeight + OVERFLOW_THRESHOLD;
    const isAtTop = el.scrollTop <= OVERFLOW_THRESHOLD;
    const isAtBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= OVERFLOW_THRESHOLD;

    setShowTopGradient(hasOverflow && !isAtTop);
    setShowBottomGradient(hasOverflow && !isAtBottom);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener("scroll", updateScrollState, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, mounted]);


  const classNames = cn(styles.simplebar_override, className);

  if (!mounted) {
    return (
      <div className={styles.scrollbar_wrapper} ref={ref}>
        <div className={classNames} {...props}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.scrollbar_wrapper} ref={ref}>
      {showGradient && showTopGradient && (
        <div className={cn(styles.gradient, styles.gradient_top)} />
      )}
      <SimpleBarReact
        className={classNames}
        autoHide={false}
        scrollableNodeProps={{ ref: scrollRef }}
        tabIndex={tabindex}
        {...props}
      >
        {children}
      </SimpleBarReact>
      {showGradient && showBottomGradient && (
        <div className={cn(styles.gradient, styles.gradient_bottom)} />
      )}
    </div>
  );
});

export default Scrollbar;
